import {AgendaOperator} from "@/agenda/lib/operators";
import {TeamMemberSchedule} from "@/core/interfaces/TeamPlanning";

/**
 * What the team configured puts members forward, it never keeps one out.
 *
 * The same order holds on every screen that names a collaborator — the
 * booking link, the online booking, the health journey and the appointment
 * form: habilitation for what is asked decides first, then having a planning
 * of one's own. A member who ticked nothing is not habilitated for
 * everything; one who has no planning is still offered, after the others.
 */

/** One collaborator as a picker offers them, in the team's order. */
export interface OperatorChoice {
    operator: AgendaOperator;
    habilitated: boolean;
    /** Lower first: habilitated with a planning, habilitated without, a planning only, neither. */
    rank: number;
}

/**
 * Whether a member ticked what is asked: the appointment type, and the
 * laboratory too when one is named. Nothing asked puts nobody forward.
 */
const isHabilitated = (
    schedule: TeamMemberSchedule | undefined,
    appointmentTypeId: number | null,
    laboratoryId: number | null
): boolean => {
    if (!schedule || (appointmentTypeId === null && laboratoryId === null)) {
        return false;
    }

    return (appointmentTypeId === null || schedule.appointment_type_ids.includes(appointmentTypeId))
        && (laboratoryId === null || schedule.referent_laboratories.some((laboratory) => laboratory.id === laboratoryId));
};

/** The whole team in the order a picker offers it, each rank keeping the order it came in. */
export const rankOperators = (
    operators: AgendaOperator[],
    schedulesByAccessId: Map<number, TeamMemberSchedule>,
    appointmentTypeId: number | null,
    laboratoryId: number | null = null
): OperatorChoice[] => {
    const choices = operators.map((operator): OperatorChoice => {
        const schedule = schedulesByAccessId.get(operator.accessId);
        const habilitated = isHabilitated(schedule, appointmentTypeId, laboratoryId);
        const planned = (schedule?.shifts.length ?? 0) > 0;

        return {operator, habilitated, rank: (habilitated ? 0 : 2) + (planned ? 0 : 1)};
    });

    // Array.prototype.sort is stable: within a rank, the team's own order holds.
    return choices.sort((a, b) => a.rank - b.rank);
};

/**
 * Who a picker lands on: the member holding the session when they stand in
 * the best rank on offer, the first of that rank otherwise.
 */
export const preferredOperator = (choices: OperatorChoice[], sessionAccessId: number | null): number | null => {
    const best = choices[0];

    if (!best) {
        return null;
    }

    const session = choices.find((choice) => choice.operator.accessId === sessionAccessId);

    return session && session.rank === best.rank ? session.operator.accessId : best.operator.accessId;
};
